import { Injectable, inject } from '@angular/core';
import {
  Auth,
  signInWithEmailAndPassword,
  signOut,
  User,
  authState,
  onAuthStateChanged,
  signInAnonymously,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from '@angular/fire/auth';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserRole, VALID_ROLES, AuthMeResponse } from '../models/user.model';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth: Auth = inject(Auth);
  private firestore: Firestore = inject(Firestore);
  private _http = inject(HttpClient);
  private cachedClientId: string | null = null;

  private _userSubject = new BehaviorSubject<User | null>(null);
  user$: Observable<User | null>;

  constructor() {
    this.auth.tenantId = environment.tenantId;
    authState(this.auth).subscribe((user) => this._userSubject.next(user));
    this.user$ = this._userSubject.asObservable();
  }

  async login(email: string, password: string) {
    try {
      const credential = await signInWithEmailAndPassword(this.auth, email, password);

      return credential.user;
    } catch (error) {
      throw error;
    }
  }

  async getUserClaims(
    user: User | null = this.auth.currentUser,
  ): Promise<Record<string, any> | null> {
    if (!user) return null;

    try {
      const idTokenResult = await user.getIdTokenResult();
      return idTokenResult.claims;
    } catch (error) {
      console.error('Error obteniendo claims del usuario:', error);
      return null;
    }
  }

  async getUserRole(user: User | null = this.auth.currentUser): Promise<UserRole | null> {
    if (!user) return null;

    try {
      const idTokenResult = await user.getIdTokenResult();
      const role = idTokenResult.claims['role'] as string;

      const normalizedRole = role?.toUpperCase();
      if (normalizedRole && VALID_ROLES.includes(normalizedRole as UserRole)) {
        return normalizedRole as UserRole;
      }
      return null;
    } catch (error) {
      console.error('Error obteniendo rol del usuario:', error);
      return null;
    }
  }

  async loginFirebase() {
    const user = await this.isLoggedIn();
    if (!user) {
      await this.signInAnonymously();
    }
  }

  isLoggedIn(): Promise<User | null> {
    return new Promise((resolve) => {
      onAuthStateChanged(
        this.auth,
        (user) => {
          if (user) {
            resolve(user);
          } else {
            resolve(null);
          }
        },
        () => resolve(null),
      );
    });
  }

  async signInAnonymously(): Promise<User> {
    return await signInAnonymously(this.auth).then(({ user }) => {
      return user;
    });
  }

  async logout() {
    signOut(this.auth);
  }

  getCurrentUser() {
    return this.auth.currentUser;
  }

  async getClientId(): Promise<string | null> {
    if (this.cachedClientId) return this.cachedClientId;

    try {
      const docSnap = await getDoc(doc(this.firestore, 'config/client-id'));
      this.cachedClientId = docSnap.data()?.['client-id'] ?? null;

      return this.cachedClientId;
    } catch (error) {
      console.error('Error obteniendo client-id:', error);
      return null;
    }
  }

  async getToken(): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) return null;

    try {
      const token = await user.getIdToken();
      return token;
    } catch (error) {
      console.error('Error obteniendo token:', error);
      return null;
    }
  }

  async getTokenWithRefresh(forceRefresh = false): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) return null;

    try {
      const token = await user.getIdToken(forceRefresh);
      return token;
    } catch (error) {
      console.error('Error obteniendo token:', error);
      return null;
    }
  }

  async refreshToken(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;

    await user.getIdToken(true);
    await user.reload();

    this._userSubject.next(this.auth.currentUser);
  }

  getMe(): Observable<AuthMeResponse> {
    return this._http.get<AuthMeResponse>(`${environment.apiUrl}/v1/auth/me`);
  }

  passwordRecovery(email: string) {
    return this._http.post(`${environment.apiUrl}/v1/auth/restablecer-password`, { email });
  }

  isSignInWithEmailLink(url: string): boolean {
    return isSignInWithEmailLink(this.auth, url);
  }

  async signInWithEmailLink(email: string, emailLink: string): Promise<User> {
    const credential = await signInWithEmailLink(this.auth, email, emailLink);
    return credential.user;
  }
}
